import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BulkUpdateTeamMembersAccessLevelSuccessResponse,
  BulkUpdateTeamMembersAccessLevelPayload,
  bulkUpdateTeamMembersAccessLevel,
} from 'TeamModule/services/BulkUpdateTeamMembersAccessLevelApi';
import { memberQueryKeys } from 'TeamModule/query/membersQueryKeys';

import { AxiosResponse } from 'axios';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import { useNotification } from 'hooks/useNotification';
import ApiClientManager from 'core/apiClient';
import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';

export interface BulkUpdateTeamMembersAccessLevelOptions {
  /** Tenant name for API context - if not provided, falls back to global tenant */
  tenant?: string;
  /** Realm ID for API context - if not provided, falls back to global realm */
  realmId?: string;
}

export const useBulkUpdateTeamMembersAccessLevel = (
  options?: BulkUpdateTeamMembersAccessLevelOptions,
) => {
  const token = useSelector(selectAuthToken);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { tenant, realmId } = options || {};

  const mutation = useMutation<
    AxiosResponse<BulkUpdateTeamMembersAccessLevelSuccessResponse>,
    unknown,
    BulkUpdateTeamMembersAccessLevelPayload
  >({
    mutationFn: async (params) => {
      if (!token) {
        throw new Error('No auth token available');
      }
      // Build custom headers if tenant/realmId are provided to ensure correct API context
      const config =
        tenant && realmId
          ? { headers: ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId }) }
          : {};
      return bulkUpdateTeamMembersAccessLevel({ params, token, config }) as Promise<
        AxiosResponse<BulkUpdateTeamMembersAccessLevelSuccessResponse>
      >;
    },
    onSuccess: () => {
      showSuccess('Update member role successful!');
      queryClient.invalidateQueries({ queryKey: memberQueryKeys.all });
    },
    onError: (error) => {
      showError(`Update failed: ${extractApiErrorMessage(error)}`);
    },
  });
  return {
    bulkUpdateTeamMembersAccessLevel: mutation.mutate,
    isBulkUpdatingTeamMembersAccessLevel: mutation.isPending,
  };
};
